import { useEffect, useState } from 'react';
import axios from 'axios';
import { Bell, Menu, X } from "lucide-react";
import toast from 'react-hot-toast';

function Header({ email, onLogout }) {
    const API_URL = process.env.REACT_APP_API_URL;
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [pendingApprovals, setPendingApprovals] = useState([]);
    const [isChangePwdOpen, setIsChangePwdOpen] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSubmittingPwd, setIsSubmittingPwd] = useState(false);

    // Fetch pending approvals
    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                axios.defaults.withCredentials = true;
                const approvalsRes = await axios.get(`${API_URL}/dashboard/pending-approvals`, { withCredentials: true });
                setPendingApprovals(approvalsRes.data);
            } catch (error) {                
            }
        };
        // listen for external updates (e.g. after editing customer)
        const onPendingApprovalsUpdated = () => {
            if (email) fetchDashboardData();
        };

        window.addEventListener('pending-approvals-updated', onPendingApprovalsUpdated);

        if (email) fetchDashboardData();

        return () => {
            window.removeEventListener('pending-approvals-updated', onPendingApprovalsUpdated);
        };
    }, [API_URL, email]);
    

    return (
        <header className="sticky top-0 z-50">
            {/* Top blue bar with company name and user info */}
            <div className="bg-blue-900 text-white py-2">
                <div className="container mx-auto flex justify-between items-center px-4 max-w-none w-full">
                    <h1 className="text-xl font-bold">
                        <a href='/'>บริษัท เอไอ บิสซิเนส เทคโนโลยี จำกัด</a>
                    </h1>
                    
                   

                    {/* User info for desktop */}
                    {email && (
                        <div className="hidden md:flex items-center space-x-4">
                            <span className="text-white font-medium">
                                Welcome, {email}
                            </span> <span>|</span>
                            <button
                                onClick={() => setIsChangePwdOpen(true)}
                                className="flex items-center space-x-1 text-white hover:text-yellow-200 font-medium"
                            >
                                <span>เปลี่ยนรหัสผ่าน</span>
                            </button>
                            <button
                                onClick={onLogout}
                                className="flex items-center space-x-1 text-white hover:text-red-300 font-medium"
                            >
                                <span role="img" aria-label="logout">🔒</span>
                                <span>Logout</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
    
            {/* White navigation bar with logo and notifications */}
            <div className="bg-white py-4 shadow-md">
                <div className="container mx-auto flex justify-between items-center px-4 max-w-none w-full">
                    <div className="flex items-center">
                        <a href="/">
                            <img src="/logo.png" alt="AI Business Technology Co., Ltd." className="h-10 w-auto" />
                        </a>
                    </div>
    
                    {/* Notifications section */}
                    <nav className="hidden md:block">
                        {email && (
                            <div className="flex items-center">
                                <a href="/panel/#pending_approvals">
                                    <div className="flex items-center text-gray-700 bg-gray-100 px-4 py-2 rounded-lg">
                                        <Bell className="mr-2 text-blue-900" />
                                        <span>รอการอนุมัติ</span>
                                        {pendingApprovals.length > 0 && (
                                            <span className="ml-2 bg-red-500 text-white px-2 py-0.5 rounded-full text-xs">
                                                {pendingApprovals.length}
                                            </span>
                                        )}
                                    </div>
                                </a>
                            </div>
                        )}
                    </nav>

                    {/* Mobile menu toggle button */}
                    <button
                        className="md:hidden text-gray-700 hover:text-blue-500"
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                    >
                        {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            {/* Change Password Modal */}
            {isChangePwdOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-4">เปลี่ยนรหัสผ่าน</h3>
                        <form onSubmit={async (e) => {
                            e.preventDefault();
                            if (isSubmittingPwd) return;
                            if (!currentPassword || !newPassword) {
                                toast.error('กรุณากรอกรหัสผ่านให้ครบถ้วน');
                                return;
                            }
                            if (newPassword !== confirmPassword) {
                                toast.error('รหัสผ่านใหม่ไม่ตรงกัน');
                                return;
                            }
                            if (newPassword.length < 8) {
                                toast.error('รหัสผ่านใหม่ต้องมีอย่างน้อย 8 ตัวอักษร');
                                return;
                            }

                            try {
                                setIsSubmittingPwd(true);
                                axios.defaults.withCredentials = true;
                                const res = await axios.post(`${API_URL}/auth/change-password`, {
                                    currentPassword,
                                    newPassword
                                }, { withCredentials: true });
                                if (res.data && res.data.success) {
                                    toast.success(res.data.message || 'เปลี่ยนรหัสผ่านเรียบร้อยแล้ว');
                                    setIsChangePwdOpen(false);
                                    setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
                                } else {
                                    toast.error(res.data?.message || 'เปลี่ยนรหัสผ่านล้มเหลว');
                                }
                            } catch (err) {
                                const msg = err?.response?.data?.message || 'เกิดข้อผิดพลาดขณะเปลี่ยนรหัสผ่าน';
                                toast.error(msg);
                            } finally {
                                setIsSubmittingPwd(false);
                            }
                        }}>
                            <div className="mb-3">
                                <label className="block text-sm font-medium mb-1">รหัสผ่านปัจจุบัน</label>
                                <input
                                    type="password"
                                    autoComplete="current-password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    className="w-full p-2 border rounded"
                                    required
                                />
                            </div>
                            <div className="mb-3">
                                <label className="block text-sm font-medium mb-1">รหัสผ่านใหม่</label>
                                <input
                                    type="password"
                                    autoComplete="new-password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="w-full p-2 border rounded"
                                    required
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">ยืนยันรหัสผ่านใหม่</label>
                                <input
                                    type="password"
                                    autoComplete="new-password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="w-full p-2 border rounded"
                                    required
                                />
                            </div>
                            <div className="flex justify-end space-x-2">
                                <button type="button" onClick={() => { setIsChangePwdOpen(false); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }} className="px-3 py-2 bg-gray-200 rounded">ยกเลิก</button>
                                <button type="submit" className="px-3 py-2 bg-blue-600 text-white rounded">{isSubmittingPwd ? 'กำลังส่ง...' : 'บันทึก'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Mobile menu */}
            {isMenuOpen && (
                <div className="bg-gray-100 shadow-md md:hidden">
                    <nav className="flex flex-col space-y-2 p-4">
                        <a href="/panel" className="text-gray-700 hover:text-blue-500">แดชบอร์ด</a>
                        <a href="/panel/course" className="text-gray-700 hover:text-blue-500">หลักสูตรอบรม</a>
                        <a href="/panel/channel" className="text-gray-700 hover:text-blue-500">ช่องทางการรับรู้</a>
                        <a href="/panel/employee" className="text-gray-700 hover:text-blue-500">ข้อมูลพนักงาน</a>
                        <div className="flex flex-col space-y-2">
                            <button
                                onClick={() => setIsChangePwdOpen(true)}
                                className="text-gray-700 hover:text-yellow-600 text-left"
                            >เปลี่ยนรหัสผ่าน
                            </button>

                            <a href="/panel/#pending_approvals" className="flex items-center text-gray-700 hover:text-blue-500">
                                <Bell className="mr-2 text-blue-900" />
                                <span>รอการอนุมัติ</span>
                                {pendingApprovals.length > 0 && (
                                    <span className="ml-2 bg-red-500 text-white px-2 py-0.5 rounded-full text-xs">
                                        {pendingApprovals.length}
                                    </span>
                                )}
                            </a>

                            <button
                                onClick={onLogout}
                                className="text-red-500 hover:text-red-700 text-left"
                            >
                                🔒 Logout
                            </button>
                        </div>
                    </nav>
                </div>
            )}
        </header>
    );
}

export default Header;